/**
 * PostFXManager — attach and control the Cinematic filter on a scene's camera.
 *
 * Central place to:
 *   - Attach the cinematic filter once per scene
 *   - Apply per-scene presets (gameplay / boss / menu / etc.)
 *   - Pulse chromatic/vignette for damage, ult, and timer critical
 *   - Ramp combo shimmer with current multiplier
 *
 * Usage:
 *   PostFXManager.attach(this, 'gameplay');
 *   PostFXManager.get(this)?.setComboShimmer(0.8);
 *   PostFXManager.get(this)?.pulseDanger(300);
 */

import Phaser from 'phaser';
import { CinematicFilter } from '@/pipelines/CinematicPipeline';
import { graphicsState } from '@/config/constants';

type Preset = 'gameplay' | 'boss' | 'menu' | 'stageClear' | 'gameOver';

export class PostFXManager {
  private static handles = new WeakMap<Phaser.Scene, PostFXManager>();

  private scene: Phaser.Scene;
  private pipeline?: CinematicFilter;
  private dangerTween?: Phaser.Tweens.Tween;

  private constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Attach the Cinematic pipeline to the main camera with the given preset. */
  static attach(scene: Phaser.Scene, preset: Preset): PostFXManager | undefined {
    try {
      // Respect graphics quality — on low, skip post-fx entirely.
      const cfg = graphicsState.getConfig?.();
      if (cfg && cfg.postFX === false) return undefined;

      let mgr = PostFXManager.handles.get(scene);
      if (!mgr) {
        mgr = new PostFXManager(scene);
        PostFXManager.handles.set(scene, mgr);
      }
      mgr.attachInternal(preset);
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        mgr!.detach();
        PostFXManager.handles.delete(scene);
      });
      return mgr;
    } catch (e) {
      console.warn('PostFXManager.attach failed (pipeline may not be registered):', e);
      return undefined;
    }
  }

  static get(scene: Phaser.Scene): PostFXManager | undefined {
    return PostFXManager.handles.get(scene);
  }

  private attachInternal(preset: Preset): void {
    if (!this.scene.cameras?.main) return;
    const cam = this.scene.cameras.main;
    try {
      const pipe = CinematicFilter.attachTo(cam);
      pipe.applyPreset(preset);
      this.pipeline = pipe;
    } catch (e) {
      console.warn('⚠️ CinematicFilter attach failed:', e);
    }
  }

  detach(): void {
    if (this.pipeline) {
      try {
        this.scene.cameras?.main?.filters?.internal.remove(this.pipeline);
      } catch {}
    }
    this.pipeline = undefined;
  }

  /** Smoothly ramp combo shimmer (call with combo-multiplier / 10). */
  setComboShimmer(v: number): void {
    this.pipeline?.setComboShimmer(v);
  }

  /** Crimson vignette & chromatic pulse — for damage, ult hits, timer critical. */
  pulseDanger(durationMs: number = 350, peak: number = 1.0): void {
    if (!this.pipeline) return;
    const pipe = this.pipeline;
    this.dangerTween?.stop();
    const obj = { v: 0 };
    this.dangerTween = this.scene.tweens.add({
      targets: obj,
      v: peak,
      duration: durationMs * 0.35,
      ease: 'Quad.easeOut',
      yoyo: true,
      hold: durationMs * 0.15,
      onUpdate: () => pipe.setDangerPulse(obj.v),
      onComplete: () => pipe.setDangerPulse(0),
    });
  }

  /** Continuously held crimson state — e.g. boss phase 3 enraged. */
  setDangerHold(v: number): void {
    this.pipeline?.setDangerPulse(v);
  }

  /** Brief ULT flash — massive bloom + chromatic spike. */
  ultFlash(durationMs: number = 600): void {
    if (!this.pipeline) return;
    const pipe = this.pipeline;
    const originalBloomThreshold = 0.68;
    pipe.setBloom(1.8, 0.25);
    pipe.setChromatic(6);
    pipe.setGrain(0.08);
    this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: durationMs,
      ease: 'Quad.easeIn',
      onUpdate: (tween) => {
        const t = tween.getValue() ?? 0;
        const inv = 1 - t;
        pipe.setBloom(0.55 + inv * 1.3, originalBloomThreshold - inv * 0.4);
        pipe.setChromatic(0.6 + inv * 5.4);
        pipe.setGrain(0.03 + inv * 0.05);
      },
    });
  }

  getPipeline(): CinematicFilter | undefined {
    return this.pipeline;
  }
}
